import { Box, Typography } from '@mui/material'
import { FeatureFlagIdBulkActionsV2 } from 'c1gen/features'
import { BulkactionV1BulkActionServiceCreateBulkAction, DirectoryV1DirectoryServiceCreate } from 'c1gen/permissions'
import { BulkActionDropdown, BulkActionOption } from 'components/core/bulk-actions/BulkActionDropdown'
import { BulkAppAssignOwnersDialog } from 'components/core/bulk-actions/BulkAppAssignOwnersDialog'
import { DynamicHeightSizer } from 'components/core/dynamic-height/DynamicHeightSizer'
import { C1EmptyIndicator } from 'components/core/empty-indicator/C1EmptyIndicator'
import { EmptyList } from 'components/core/empty-list'
import { C1Link } from 'components/core/links/C1Link'
import List from 'components/core/list'
import { C1TableReduxColumn } from 'components/core/list/C1Table/C1TableRedux'
import { C1TableReduxHook } from 'components/core/list/C1Table/C1TableReduxHook'
import { C1MultiUserDisplay } from 'components/core/multi-user/C1MultiUserDisplay'
import { useFeatureChecker, useHasBuiltinRole, useHasPermission } from 'components/no-ui/capability'
import { moduleStyleType } from 'components/no-ui/styles/styleTypes'
import { AppDisplayName } from 'components/pages/app/app-display-name'
import { AppSearchFilter } from 'components/pages/app/users/details/search/apps'
import { ExportData } from 'components/pages/explore/query/ExportData'
import { AppValue } from 'lib/actions/apps'
import { RoleID } from 'lib/constants/system'
import { UserContext } from 'lib/context/app'
import { routes } from 'lib/routes'
import { useInitialFilters } from 'lib/search'
import { useRouter } from 'next/navigation'
import { SearchAppsExpandedRequest } from 'pbts/c1/api/app/v1/app'
import { ExportDataRequest, QueryId } from 'pbts/c1/api/exportdata/v1/exportdata'
import { FC, useCallback, useContext, useMemo, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { appSearchApi } from 'redux_toolkit/apps'
import { AppUserCount } from './AppUserCount'

const PageStyles: moduleStyleType = {
    content: { display: 'flex', flexDirection: 'column', flex: 1, overflow: 'auto' },
}

export const formatAppSubtitle = (app: AppValue): string | React.ReactElement => {
    if (!app.appAccountId && !app.appAccountName) {
        return <C1EmptyIndicator />
    }

    let appSubtitle = app.appAccountName
    if (app.appAccountId) {
        if (appSubtitle !== '') {
            appSubtitle += ' '
        }
        appSubtitle += `#${app.appAccountId}`
    }

    return appSubtitle
}

const AppListEmptyText = ({ filter }: { filter: SearchAppsExpandedRequest }): React.ReactElement => {
    const hasPermission = useHasPermission(DirectoryV1DirectoryServiceCreate)

    if (hasPermission && !filter.query) {
        return <EmptyList
            title={<FormattedMessage
                id="components_pages_applications_no_apps_title"
                defaultMessage="You don't have any applications."
            />}
            text={<FormattedMessage
                id="components_pages_applications_no_apps"
                defaultMessage="Get started by {connect_directory_link}."
                values={{
                    connect_directory_link: (
                        <C1Link href={routes.integration}>
                            <FormattedMessage
                                id="connect_directory"
                                defaultMessage="connecting your directory"
                            />
                        </C1Link>
                    ),
                }}
            />}
        />
    }
    return (
        <Typography>
            <FormattedMessage id="components_app_list_no_apps_found" defaultMessage="No applications found." />
        </Typography>
    )
}

export const cols: C1TableReduxColumn<AppValue>[] = [
    {
        field: 'app',
        label: <FormattedMessage id="components_app_list_name" defaultMessage="Application" />,
        Component: ({ row }) => <AppDisplayName app={row} margin={false} />,
    },
    {
        field: 'source',
        label: <FormattedMessage id="components_app_list_source" defaultMessage="Source" />,
        Component: ({ row }) => (row.parentApp
            ? <AppDisplayName app={row.parentApp} margin={false} />
            : <C1EmptyIndicator />),
    },
    {
        field: 'app_owner',
        label: <FormattedMessage id="components_app_list_owners" defaultMessage="App owners" />,
        Component: ({ row }) => (row.appOwners && row.appOwners.length > 0
            ? <C1MultiUserDisplay users={row.appOwners} />
            : <C1EmptyIndicator />),
    },
    {
        field: 'details',
        label: <FormattedMessage id="components_app_list_description" defaultMessage="Details" />,
        Component: ({ row }) => <Typography>{formatAppSubtitle(row)}</Typography>,
    },
    {
        field: 'user_count',
        label: <FormattedMessage id="components_app_list_user_count" defaultMessage="Accounts" />,
        Component: ({ row }) => <AppUserCount app={row} />,
    },
]

export const ApplicationList: FC = () => {
    const initialFilters = useInitialFilters(SearchAppsExpandedRequest, [])
    const hasBulkActionsV2 = useFeatureChecker(FeatureFlagIdBulkActionsV2)
    const user = useContext(UserContext)
    const router = useRouter()

    const isReadOnlySuperAdmin = useHasBuiltinRole(RoleID.ReadOnlySuperAdmin)
    const isSuperAdmin = useHasBuiltinRole(RoleID.SuperAdmin)
    const isReadOnlySuperAdminOrSuperAdmin = isReadOnlySuperAdmin || isSuperAdmin

    const appOwnerIds = isReadOnlySuperAdminOrSuperAdmin ? [] : [user?.user.id]

    const [filter, setFilter] = useState<SearchAppsExpandedRequest>(
        SearchAppsExpandedRequest.create({
            ...initialFilters,
            appOwnerIds,
            expandMask: { paths: ['*'] },
        }),
    )

    const onFilterChange = useCallback(
        (filterUpdate: Partial<SearchAppsExpandedRequest>) => {
            setFilter((old) => {
                const updated = SearchAppsExpandedRequest.create(old)
                SearchAppsExpandedRequest.mergePartial(updated, filterUpdate)
                if (SearchAppsExpandedRequest.equals(updated, old)) {
                    return old
                }
                return updated
            })
        },
        [setFilter],
    )

    const [selectedApps, setSelectedApps] = useState<AppValue[]>([])

    const canCreateBulkAction = useHasPermission(BulkactionV1BulkActionServiceCreateBulkAction)

    const options: BulkActionOption<AppValue, object>[] = useMemo(() => (canCreateBulkAction ? [
        {
            key: 'assignOwners',
            label: <FormattedMessage id="bulk_action_set_owners" defaultMessage="Set owners" />,
            behavior: {
                type: 'dialog',
                Dialog: BulkAppAssignOwnersDialog,
            },
        },
    ] : []), [canCreateBulkAction])

    const table = useMemo(() => (
        <C1TableReduxHook<SearchAppsExpandedRequest, AppValue>
            cols={cols}
            request={filter}
            hook={appSearchApi.useSearchAppsExpanded}
            invalidateHook={appSearchApi.useInvalidateSearchAppsExpanded}
            hookFlags={{ notify: true, autoReload: true }}
            emptyState={<AppListEmptyText filter={filter} />}
            selections={hasBulkActionsV2 ? selectedApps : undefined}
            setSelections={hasBulkActionsV2 ? setSelectedApps : undefined}
            getId={(app): string => app.id}
            extraFooterActions={hasBulkActionsV2 ?
                <BulkActionDropdown<AppValue> selections={selectedApps} setSelections={setSelectedApps} options={options} />
                : undefined
            }
            onRowClick={(_, app): void => {
                router.push(routes.applications.detail(app.id))
            }}
        />
    ), [
        filter,
        hasBulkActionsV2,
        router,
        selectedApps,
        setSelectedApps,
        options,
    ])

    const exportDataRequest = useMemo(() => ExportDataRequest.create({
        queryId: QueryId.ALL_APPS,
        request: {
            oneofKind: 'searchAppsExpandedRequest',
            searchAppsExpandedRequest: SearchAppsExpandedRequest.create(filter),
        },
    }), [filter])

    const search = useMemo(() => <AppSearchFilter setAppsFilter={onFilterChange} />, [onFilterChange])

    return (
        <Box sx={PageStyles.content}>
            <DynamicHeightSizer>
                <List<AppValue>
                    table={table}
                    search={search}
                    buttons={<ExportData exportDataRequest={exportDataRequest} />}
                />
            </DynamicHeightSizer>
        </Box>
    )
}
